import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import { Shield, FileText, Cookie, ChevronRight } from 'lucide-react';

const PolicyPage = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('privacy');

  const sections = [
    { id: 'privacy', title: t('policy.privacy'), icon: Shield },
    { id: 'terms', title: t('policy.terms'), icon: FileText },
    { id: 'cookies', title: t('policy.cookies'), icon: Cookie }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>
        <motion.div 
          className="max-w-5xl mx-auto text-center relative"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">{t('policy.title')}</h1>
          <p className="text-lg sm:text-xl text-blue-100 max-w-3xl mx-auto">
            {t('policy.subtitle')}
          </p>
        </motion.div>
      </section>

      {/* Policy Navigation */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-b sticky top-16 z-30 shadow-sm">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap gap-3">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <motion.button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 border-2 ${
                    activeSection === section.id
                      ? 'bg-blue-900 text-white border-blue-900 shadow-lg'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-900 hover:text-blue-900'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Icon className="w-4 h-4" />
                  {section.title}
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Privacy Policy */}
          {activeSection === 'privacy' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="border-2">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-white border-b-2">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-900 rounded-lg">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-3xl text-blue-900">Privacy Policy</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">Last updated: January 24, 2025</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-8 space-y-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Introduction
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      OL-CONSULT SARL ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Information We Collect
                    </h3>
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Personal Information</h4>
                        <p>We may collect personal information that you provide to us, including:</p>
                        <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                          <li>Name, email address, and phone number</li>
                          <li>Company name and business information</li>
                          <li>Communication preferences</li>
                          <li>Any other information you choose to provide</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Automatically Collected Information</h4>
                        <p>When you visit our website, we may automatically collect:</p>
                        <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                          <li>IP address and browser type</li>
                          <li>Device information and operating system</li>
                          <li>Pages visited and time spent on our site</li>
                          <li>Referring website addresses</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      How We Use Your Information
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-3">We use the information we collect to:</p>
                    <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                      <li>Provide, maintain, and improve our services</li>
                      <li>Respond to your inquiries and provide customer support</li>
                      <li>Send you newsletters, updates, and promotional materials (with your consent)</li>
                      <li>Analyze website usage and optimize user experience</li>
                      <li>Comply with legal obligations and protect our rights</li>
                      <li>Prevent fraud and enhance security</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Information Sharing and Disclosure
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                    </p>
                    <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                      <li><strong>Service Providers:</strong> With trusted third-party service providers who assist in operating our website and services</li>
                      <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                      <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                      <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Data Security
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Your Rights
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-3">You have the right to:</p>
                    <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                      <li>Access and receive a copy of your personal information</li>
                      <li>Correct inaccurate or incomplete information</li>
                      <li>Request deletion of your personal information</li>
                      <li>Object to or restrict certain processing of your information</li>
                      <li>Withdraw consent at any time (where applicable)</li>
                      <li>Lodge a complaint with a supervisory authority</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Contact Us
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      If you have questions or concerns about this Privacy Policy, please contact us at:
                    </p>
                    <div className="mt-3 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-900">
                      <p className="font-semibold text-gray-900">OL-CONSULT SARL</p>
                      <p className="text-gray-700">Email: contact@ol-consult.com</p>
                      <p className="text-gray-700">Phone: +243 832590522</p>
                      <p className="text-gray-700">Address: 8314 Boulevard Du 30 Juin, Gombe, Kinshasa, DRC</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Terms of Service */}
          {activeSection === 'terms' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="border-2">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-white border-b-2">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-900 rounded-lg">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-3xl text-blue-900">Terms of Service</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">Last updated: January 24, 2025</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-8 space-y-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Acceptance of Terms
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      By accessing and using the OL-CONSULT website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Our Services
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      OL-CONSULT SARL provides business consulting, investment advisory, and project management services. Our services include:
                    </p>
                    <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                      <li>Business strategy and planning consultation</li>
                      <li>Investment opportunity analysis and advisory</li>
                      <li>Market research and feasibility studies</li>
                      <li>Project management and implementation support</li>
                      <li>Sector-specific expertise in mining, real estate, and infrastructure</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      User Responsibilities
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-3">When using our services, you agree to:</p>
                    <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                      <li>Provide accurate and complete information</li>
                      <li>Maintain the confidentiality of your account credentials</li>
                      <li>Use our services only for lawful purposes</li>
                      <li>Not interfere with or disrupt our services</li>
                      <li>Respect intellectual property rights</li>
                      <li>Comply with all applicable laws and regulations</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Intellectual Property
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      All content on this website, including text, graphics, logos, images, and software, is the property of OL-CONSULT SARL or its content suppliers and is protected by international copyright laws. You may not reproduce, distribute, or create derivative works without our express written permission.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Limitation of Liability
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      OL-CONSULT SARL provides consulting services based on information available at the time. We make every effort to ensure accuracy, but:
                    </p>
                    <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                      <li>We do not guarantee specific results or outcomes</li>
                      <li>Our advice should not be the sole basis for business decisions</li>
                      <li>We are not liable for indirect, incidental, or consequential damages</li>
                      <li>Our total liability is limited to the fees paid for our services</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Confidentiality
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      We maintain strict confidentiality regarding client information and project details. All proprietary information shared during consulting engagements will be protected according to our confidentiality agreements and professional standards.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Termination
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      We reserve the right to terminate or suspend access to our services immediately, without prior notice, for any reason, including breach of these Terms of Service.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Governing Law
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      These Terms of Service are governed by the laws of the Democratic Republic of Congo. Any disputes arising from these terms shall be resolved in the courts of Kinshasa, DRC.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Changes to Terms
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      We may update these Terms of Service from time to time. We will notify you of any changes by posting the new terms on this page with an updated "Last updated" date. Your continued use of our services after changes constitutes acceptance of the new terms.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Cookie Policy */}
          {activeSection === 'cookies' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="border-2">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-white border-b-2">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-900 rounded-lg">
                      <Cookie className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-3xl text-blue-900">Cookie Policy</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">Last updated: January 24, 2025</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-8 space-y-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      What Are Cookies?
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Types of Cookies We Use
                    </h3>
                    <div className="space-y-4 text-gray-700">
                      <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-900">
                        <h4 className="font-semibold text-gray-900 mb-2">Essential Cookies</h4>
                        <p className="leading-relaxed">
                          These cookies are necessary for the website to function properly. They enable basic functions like page navigation and access to secure areas. The website cannot function properly without these cookies.
                        </p>
                      </div>

                      <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-600">
                        <h4 className="font-semibold text-gray-900 mb-2">Performance Cookies</h4>
                        <p className="leading-relaxed">
                          These cookies collect information about how visitors use our website, such as which pages are visited most often. This data helps us optimize our website and improve user experience.
                        </p>
                      </div>

                      <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-600">
                        <h4 className="font-semibold text-gray-900 mb-2">Functionality Cookies</h4>
                        <p className="leading-relaxed">
                          These cookies allow our website to remember choices you make (such as language preferences) and provide enhanced, personalized features.
                        </p>
                      </div>

                      <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-600">
                        <h4 className="font-semibold text-gray-900 mb-2">Targeting/Advertising Cookies</h4>
                        <p className="leading-relaxed">
                          These cookies are used to deliver content more relevant to you and your interests. They may be set by us or third-party providers whose services we use on our pages.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      How We Use Cookies
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-3">We use cookies to:</p>
                    <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                      <li>Keep you signed in to your account</li>
                      <li>Remember your preferences and settings</li>
                      <li>Understand how you use our website</li>
                      <li>Improve website performance and user experience</li>
                      <li>Analyze traffic and usage patterns</li>
                      <li>Deliver relevant content and advertisements</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Third-Party Cookies
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      We may use third-party services that set cookies on our website, including analytics providers, advertising networks, and social media platforms. These third parties have their own privacy policies and cookie policies.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Managing Cookies
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      You can control and manage cookies in several ways:
                    </p>
                    <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                      <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or accept cookies through their settings</li>
                      <li><strong>Cookie Consent Tool:</strong> Use our cookie consent banner to manage your preferences</li>
                      <li><strong>Delete Cookies:</strong> You can delete cookies already stored on your device</li>
                      <li><strong>Opt-Out Tools:</strong> Use third-party opt-out tools for advertising cookies</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      <strong>Note:</strong> Blocking or deleting cookies may affect your experience on our website and limit certain features.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Updates to This Policy
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our business practices. Please check this page regularly for updates.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ChevronRight className="w-5 h-5 text-blue-900" />
                      Contact Us
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      If you have questions about our use of cookies, please contact us at:
                    </p>
                    <div className="mt-3 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-900">
                      <p className="font-semibold text-gray-900">OL-CONSULT SARL</p>
                      <p className="text-gray-700">Email: contact@ol-consult.com</p>
                      <p className="text-gray-700">Phone: +243 832590522</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PolicyPage;
